<!-- Messages toggle -->
<div [dir]="lang === 'en' ? 'ltr' : 'rtl'">
    <ng-container *transloco="let t">
        <button mat-icon-button (click)="openPanel()" #messagesOrigin>
            <ng-container *ngIf="unreadCount > 0">
                <span class="absolute top-0 right-0 left-0 flex items-center justify-center h-3">
                    <span
                        class="flex items-center justify-center shrink-0 min-w-4 h-4 px-1 ml-4 mt-2.5 rounded-full bg-primary text-xs font-medium"
                    >
                        {{ unreadCount }}
                    </span>
                </span>
            </ng-container>
            <mat-icon [svgIcon]="'heroicons_outline:bell'"></mat-icon>
        </button>

        <!-- Notifications panel -->
        <ng-template #messagesPanel>
            <div
                class="sai-notifications-panel fixed inset-0 sm:static sm:inset-auto flex flex-col sm:w-[420px] sm:min-w-[420px] sm:rounded-2xl overflow-hidden shadow-lg"
                [attr.dir]="lang === 'ar' ? 'rtl' : 'ltr'"
            >
                <!-- Header - keep primary background; justify-between (not ml-auto) so RTL puts title at inline-start and trash at inline-end -->
                <div
                    class="flex shrink-0 items-center justify-between gap-3 py-4 pe-4 ps-6 bg-primary hover:hover-pg-primary text-on-primary"
                >
                    <div class="flex items-center gap-3 min-w-0">
                        <div class="sm:hidden -ms-1">
                            <button mat-icon-button (click)="closePanel()">
                                <mat-icon
                                    class="icon-size-5 text-current"
                                    [svgIcon]="'heroicons_outline:x-mark'"
                                ></mat-icon>
                            </button>
                        </div>
                        <div class="text-lg font-semibold leading-10 text-white">
                            {{ t('Notifications') }}
                        </div>
                    </div>
                    <button
                        mat-icon-button
                        [matTooltip]="t('Remove all')"
                        [disabled]="!messages?.length"
                        (click)="deleteAllNotifications()"
                    >
                        <mat-icon
                            class="icon-size-5 text-current"
                            [svgIcon]="'heroicons_outline:trash'"
                        ></mat-icon>
                    </button>
                </div>

                <!-- Filter tabs -->
                <div class="sai-notifications-panel__surface shrink-0 px-4 pt-4 pb-2">
                    <div class="sai-notifications-panel__tablist flex items-center gap-1 p-1 rounded-xl">
                        <button
                            type="button"
                            class="sai-notifications-panel__tab flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-sm font-medium transition-colors"
                            [class.sai-notifications-panel__tab--active]="activeFilter === 'all'"
                            (click)="setActiveFilter('all')"
                        >
                            <span>{{ t('All') }}</span>
                            <span class="sai-notifications-panel__tab-badge">{{ allCount }}</span>
                        </button>
                        <button
                            type="button"
                            class="sai-notifications-panel__tab flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-sm font-medium transition-colors"
                            [class.sai-notifications-panel__tab--active]="activeFilter === 'unread'"
                            (click)="setActiveFilter('unread')"
                        >
                            <span>{{ t('Unread') }}</span>
                            <span class="sai-notifications-panel__tab-badge">{{ unreadTabCount }}</span>
                        </button>
                        <button
                            type="button"
                            class="sai-notifications-panel__tab flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-sm font-medium transition-colors"
                            [class.sai-notifications-panel__tab--active]="activeFilter === 'read'"
                            (click)="setActiveFilter('read')"
                        >
                            <span>{{ t('Read') }}</span>
                            <span class="sai-notifications-panel__tab-badge">{{ readTabCount }}</span>
                        </button>
                    </div>
                </div>

                <!-- Notification cards -->
                <div
                    class="sai-notifications-panel__surface relative flex-auto min-h-0 sm:max-h-[28rem] overflow-y-auto overflow-x-hidden px-4 py-2"
                >
                    <ng-container *ngFor="let message of filteredMessages; trackBy: trackByFn">
                        <div
                            class="sai-notifications-panel__card flex-shrink-0 mb-3 rounded-xl border"
                            [class.sai-notifications-panel__card--unread]="!message.read"
                            [class.sai-notifications-panel__card--read]="message.read"
                        >
                            <!-- Card header -->
                            <div class="flex items-start gap-3 p-3 min-h-[3.25rem]">
                                <div
                                    class="flex flex-1 items-start gap-3 min-w-0 cursor-pointer"
                                    (click)="navigateToMessage(message)"
                                >
                                    <div
                                        class="flex shrink-0 items-center justify-center w-9 h-9 rounded-full"
                                        [ngClass]="getNotificationIconStyle(message).bg"
                                    >
                                        <mat-icon
                                            class="icon-size-5"
                                            [ngClass]="getNotificationIconStyle(message).text"
                                            [svgIcon]="getNotificationIconStyle(message).icon"
                                        ></mat-icon>
                                    </div>

                                    <div class="flex flex-col flex-1 min-w-0 pt-0.5">
                                        <div
                                            class="notification-panel-text sai-notifications-panel__title font-semibold text-sm leading-snug"
                                            [class.line-clamp-2]="!isExpanded(message)"
                                            [innerHTML]="getNotificationTitle(message) || 'Notification'"
                                        ></div>
                                        <div
                                            *ngIf="!isExpanded(message) && getNotificationPreview(message)"
                                            class="notification-panel-text sai-notifications-panel__muted mt-1 text-xs line-clamp-1"
                                            [innerHTML]="getNotificationPreview(message)"
                                        ></div>
                                    </div>
                                </div>

                                <div class="flex shrink-0 items-center gap-0.5 -mr-1 -mt-0.5">
                                    <button
                                        type="button"
                                        class="sai-notifications-panel__icon-btn"
                                        mat-icon-button
                                        (click)="toggleExpand(message); $event.stopPropagation()"
                                        [matTooltip]="isExpanded(message) ? t('Collapse') : t('Expand')"
                                    >
                                        <mat-icon
                                            class="icon-size-4"
                                            [svgIcon]="isExpanded(message) ? 'heroicons_mini:chevron-up' : 'heroicons_mini:chevron-down'"
                                        ></mat-icon>
                                    </button>
                                    <button
                                        type="button"
                                        class="sai-notifications-panel__icon-btn"
                                        mat-icon-button
                                        (click)="removeOneMessage(message, $event)"
                                        [matTooltip]="t('Remove')"
                                    >
                                        <mat-icon
                                            class="icon-size-4"
                                            [svgIcon]="'heroicons_mini:x-mark'"
                                        ></mat-icon>
                                    </button>
                                </div>
                            </div>

                            <!-- Expanded body -->
                            <div
                                *ngIf="isExpanded(message)"
                                class="sai-notifications-panel__divider px-3 pb-3 pt-0 ml-12 border-t"
                            >
                                <p
                                    class="notification-panel-text sai-notifications-panel__muted text-sm leading-relaxed mt-3 mb-3 cursor-pointer hover:opacity-80"
                                    [innerHTML]="getNotificationBody(message) || getNotificationTitle(message)"
                                    (click)="navigateToMessage(message, $event)"
                                ></p>
                                <div class="flex items-center justify-between gap-3">
                                    <span class="sai-notifications-panel__muted text-xs">
                                        {{ message.createdAt | date : 'MMM dd, h:mm a' }}
                                    </span>
                                    <button
                                        type="button"
                                        mat-stroked-button
                                        class="sai-notifications-panel__okay-btn !min-h-8 !h-8 !px-4 !text-sm !rounded-lg"
                                        (click)="acknowledgeNotification(message, $event)"
                                    >
                                        {{ t('Okay') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </ng-container>

                    <!-- Empty state -->
                    <ng-container *ngIf="!filteredMessages.length">
                        <div class="flex flex-col flex-auto items-center justify-center py-12 px-6">
                            <div
                                class="flex flex-0 items-center justify-center w-14 h-14 rounded-full bg-primary/10"
                            >
                                <mat-icon
                                    class="text-primary"
                                    [svgIcon]="'heroicons_outline:bell'"
                                ></mat-icon>
                            </div>
                            <div class="sai-notifications-panel__title mt-5 text-xl font-semibold tracking-tight">
                                {{ t('No notifications yet!') }}
                            </div>
                            <div class="sai-notifications-panel__muted w-full max-w-60 mt-1 text-sm text-center">
                                {{ t('All notification will appear here.') }}
                            </div>
                        </div>
                    </ng-container>
                </div>

                <!-- Footer -->
                <div class="sai-notifications-panel__surface sai-notifications-panel__divider shrink-0 px-4 py-3 border-t">
                    <button
                        type="button"
                        class="sai-notifications-panel__title text-sm font-medium underline underline-offset-2 hover:opacity-80 disabled:opacity-40 disabled:no-underline"
                        [disabled]="unreadCount === 0"
                        (click)="markAllAsRead()"
                    >
                        {{ t('Mark all as read') }}
                    </button>
                </div>
            </div>
        </ng-template>
    </ng-container>
</div>
